import React, { useMemo, useState, useEffect } from "react";
import { bool, func } from "prop-types";
import cogoToast from "cogo-toast";
import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";
import { useDispatch, useSelector } from "react-redux";
import {
  submitHiringSupportRequest,
  getHiringSupportStatus,
  cancelHiringSupportRequest,
  getHiringSupportHistory,
} from "../redux/actions/hiringSupportActions";

/* ─── Styles ─────────────────────────────────────────────────────────────── */

const fieldWrap = { display: "flex", flexDirection: "column", marginBottom: "16px" };

const labelStyle = { fontSize: "12px", fontWeight: 400, color: "#374151", marginBottom: "4px" };

const inputBase = {
  height: "36px",
  padding: "0 10px",
  border: "1px solid #cdd2d8",
  borderRadius: "20px",
  fontSize: "13px",
  color: "#111827",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  background: "#fff",
  transition: "border-color 0.15s",
  fontFamily: "inherit",
};

const selectStyle = {
  ...inputBase,
  cursor: "pointer",
  appearance: "none",
  WebkitAppearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24'%3E%3Cpath fill='%236b7280' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "calc(100% - 10px) center",
  paddingRight: "30px",
};

const sectionHead = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#6b7280",
  marginBottom: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const btnBase = {
  height: "36px",
  padding: "0 20px",
  borderRadius: "20px",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "all 0.15s",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
};

/* ─── Status config ──────────────────────────────────────────────────────── */

const STATUS_CONFIG = {
  pending: {
    label: "Pending Review",
    color: "#d97706",
    bg: "#fef3c7",
    border: "#fcd34d",
    icon: "",
    description: "Your request has been received and is awaiting review by our team.",
    canCancel: true,
  },
  under_review: {
    label: "Under Review",
    color: "#2563eb",
    bg: "#dbeafe",
    border: "#93c5fd",
    icon: "",
    description: "Our team is currently reviewing your request and will assign a recruiter soon.",
    canCancel: false,
  },
  assigned: {
    label: "Recruiter Assigned",
    color: "#059669",
    bg: "#d1fae5",
    border: "#6ee7b7",
    icon: "",
    description: "A workbrook recruiter has been assigned to your request and will reach out within 24 hours.",
    canCancel: false,
  },
};

const VALID_STATUSES = Object.keys(STATUS_CONFIG);

/* ─── Primitives ─────────────────────────────────────────────────────────── */

const Field = ({ label, required, error, children }) => (
  <div style={fieldWrap}>
    <label style={labelStyle}>
      {label}
      {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {error && (
      <span style={{ fontSize: "11px", color: "#ef4444", marginTop: "4px", paddingLeft: "10px" }}>
        {error}
      </span>
    )}
  </div>
);

const TextInput = ({ name, value, onChange, placeholder, type = "text", required, hasError }) => (
  <input
    type={type}
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder || ""}
    required={required}
    style={{
      ...inputBase,
      borderColor: hasError ? "#ef4444" : "#cdd2d8",
    }}
    onFocus={(e) => (e.target.style.borderColor = hasError ? "#ef4444" : "#3A6ED4")}
    onBlur={(e) => (e.target.style.borderColor = hasError ? "#ef4444" : "#cdd2d8")}
  />
);

const SelectInput = ({ name, value, onChange, options }) => (
  <select
    name={name}
    value={value}
    onChange={onChange}
    style={selectStyle}
    onFocus={(e) => (e.target.style.borderColor = "#3A6ED4")}
    onBlur={(e) => (e.target.style.borderColor = "#cdd2d8")}
  >
    {options.map(({ value: v, label }) => (
      <option key={v} value={v}>{label}</option>
    ))}
  </select>
);

const CheckItem = ({ label, checked, onChange }) => (
  <label style={{
    display: "flex", alignItems: "center", gap: "8px",
    fontSize: "13px", color: "#374151", cursor: "pointer",
    marginBottom: "8px", userSelect: "none",
  }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ width: "14px", height: "14px", accentColor: "#3A6ED4", cursor: "pointer", flexShrink: 0 }}
    />
    {label}
  </label>
);

/* ─── History View ───────────────────────────────────────────────────────── */

const HistoryView = ({ history, loading, onBack, onClose }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || { label: status, color: "#6b7280", bg: "#f3f4f6", border: "#d1d5db" };
    return (
      <span style={{
        display: "inline-flex",
        padding: "4px 12px",
        borderRadius: "12px",
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        fontSize: "11px",
        fontWeight: 600,
      }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <div style={{ padding: "24px", maxWidth: "680px", margin: "0 auto" }}>
      <div style={{ marginBottom: "24px" }}>
        <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#111827", margin: "0 0 8px 0" }}>
          Request History
        </h3>
        <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
          View all your past hiring support requests
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div style={{ fontSize: "13px", color: "#6b7280" }}>Loading history...</div>
        </div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
            No previous requests found
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {history.map((req) => (
            <div key={req.id} style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "16px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827", marginBottom: "4px" }}>
                    {req.role_title}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {req.company_name || "No company specified"}
                  </div>
                </div>
                {getStatusBadge(req.status)}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "8px", fontSize: "11px", color: "#6b7280" }}>
                {req.seniority && (
                  <div>
                    <span style={{ fontWeight: 600 }}>Level:</span> {req.seniority}
                  </div>
                )}
                {req.location && (
                  <div>
                    <span style={{ fontWeight: 600 }}>Location:</span> {req.location}
                  </div>
                )}
                {req.timeline && (
                  <div>
                    <span style={{ fontWeight: 600 }}>Timeline:</span> {req.timeline}
                  </div>
                )}
                <div>
                  <span style={{ fontWeight: 600 }}>Submitted:</span> {formatDate(req.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: "24px", display: "flex", gap: "12px", justifyContent: "center" }}>
        <button
          onClick={onBack}
          style={{ ...btnBase, background: "#3A6ED4", border: "none", color: "#fff" }}
        >
          Back
        </button>
        <button
          onClick={onClose}
          style={{ ...btnBase, background: "#f3f4f6", border: "none", color: "#374151" }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

/* ─── Status View ────────────────────────────────────────────────────────── */

const StatusView = ({ status, onCancel, onClose, onRequestNew, onViewHistory, cancelling }) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const cfg = STATUS_CONFIG[status];

  // Guard: if status is not recognised, don't crash
  if (!cfg) {
    return (
      <div style={{ padding: "32px 24px", textAlign: "center" }}>
        <p style={{ fontSize: "14px", color: "#6b7280" }}>
          No active hiring support request found.
        </p>
        <button
          onClick={onClose}
          style={{ ...btnBase, background: "#3A6ED4", border: "none", color: "#fff", marginTop: "16px" }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "32px 24px", maxWidth: "560px", margin: "0 auto" }}>

      {/* Status badge */}
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        textAlign: "center", marginBottom: "32px",
      }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>{cfg.icon}</div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          padding: "6px 16px", borderRadius: "20px",
          background: cfg.bg, border: `1px solid ${cfg.border}`,
          color: cfg.color, fontSize: "13px", fontWeight: 700,
          marginBottom: "16px",
        }}>
          {cfg.label}
        </div>
        <p style={{ fontSize: "14px", color: "#374151", lineHeight: 1.6, margin: 0 }}>
          {cfg.description}
        </p>
      </div>

      {/* Timeline */}
      <div style={{
        background: "#f9fafb", borderRadius: "12px",
        padding: "16px 20px", marginBottom: "24px",
      }}>
        <div style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "12px" }}>
          Request Timeline
        </div>
        {["pending", "under_review", "assigned"].map((s, i) => {
          const steps = ["pending", "under_review", "assigned"];
          const currentIndex = steps.indexOf(status);
          const stepIndex = i;
          const isDone = stepIndex <= currentIndex;
          const isCurrent = stepIndex === currentIndex;

          return (
            <div key={s} style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: i < 2 ? "10px" : 0 }}>
              {/* Dot */}
              <div style={{ flexShrink: 0, marginTop: "2px" }}>
                <div style={{
                  width: "16px", height: "16px", borderRadius: "50%",
                  background: isDone ? STATUS_CONFIG[s].color : "#e5e7eb",
                  border: isCurrent ? `2px solid ${STATUS_CONFIG[s].color}` : "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {isDone && (
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="white">
                      <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
                    </svg>
                  )}
                </div>
              </div>
              {/* Label */}
              <div>
                <div style={{ fontSize: "12px", fontWeight: isCurrent ? 700 : 500, color: isDone ? "#111827" : "#9ca3af" }}>
                  {STATUS_CONFIG[s].label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cancel option — only for pending */}
      {cfg.canCancel && !showCancelConfirm && (
        <div style={{ textAlign: "center", marginBottom: "16px" }}>
          <button
            onClick={() => setShowCancelConfirm(true)}
            disabled={cancelling}
            style={{
              ...btnBase,
              background: "transparent",
              border: "1.5px solid #fca5a5",
              color: "#ef4444",
              fontSize: "12px",
              opacity: cancelling ? 0.7 : 1,
              cursor: cancelling ? "not-allowed" : "pointer",
            }}
          >
            {cancelling ? "Cancelling..." : "Cancel Request"}
          </button>
        </div>
      )}

      {/* Cancel confirmation */}
      {showCancelConfirm && (
        <div style={{
          background: "#fff5f5", border: "1px solid #fca5a5",
          borderRadius: "10px", padding: "16px", marginBottom: "16px", textAlign: "center",
        }}>
          <p style={{ fontSize: "13px", color: "#b91c1c", marginBottom: "12px", marginTop: 0 }}>
            Are you sure you want to cancel this request? This cannot be undone.
          </p>
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            <button
              onClick={() => setShowCancelConfirm(false)}
              disabled={cancelling}
              style={{ ...btnBase, background: "#fff", border: "1.5px solid #d1d5db", color: "#374151" }}
            >
              Keep Request
            </button>
            <button
              onClick={() => { onCancel(); setShowCancelConfirm(false); }}
              disabled={cancelling}
              style={{
                ...btnBase,
                background: "#ef4444",
                border: "none",
                color: "#fff",
                opacity: cancelling ? 0.7 : 1,
                cursor: cancelling ? "not-allowed" : "pointer",
              }}
            >
              {cancelling ? "Cancelling..." : "Yes, Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* Request New Support - only for assigned status */}
      {status === "assigned" && (
        <div style={{ textAlign: "center", marginBottom: "16px" }}>
          <button
            onClick={onRequestNew}
            style={{
              ...btnBase,
              background: "#3A6ED4",
              border: "none",
              color: "#fff",
              padding: "0 24px",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Request New Support
          </button>
        </div>
      )}

      {/* View History */}
      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <button
          onClick={onViewHistory}
          style={{
            ...btnBase,
            background: "transparent",
            border: "1.5px solid #d1d5db",
            color: "#374151",
            fontSize: "12px",
          }}
        >
          View Request History
        </button>
      </div>

      {/* Close */}
      <div style={{ textAlign: "center" }}>
        <button
          onClick={onClose}
          style={{ ...btnBase, background: "#f3f4f6", border: "none", color: "#374151", padding: "0 32px" }}
        >
          Close
        </button>
      </div>

    </div>
  );
};

const RequestHiringSupport = ({ open, onClose, inline = false }) => {
  const dispatch = useDispatch();
  const hiringSupportState = useSelector((state) => state.hiringSupport);
  const submitting = hiringSupportState?.submitting;
  const cancelling = hiringSupportState?.cancelling;

  const loadingStatus = hiringSupportState?.loadingStatus;
  const loadingHistory = hiringSupportState?.loadingHistory;
  const reduxRequest = hiringSupportState?.request;
  const history = hiringSupportState?.history || [];

  const [statusChecked, setStatusChecked] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [forceForm, setForceForm] = useState(false);

  const [form, setForm] = useState({
    fullName: "", email: "", phone: "", companyName: "",
    roleTitle: "", seniority: "", location: "",
    confidentiality: "", timeline: "", otherReason: "",
  });

  const [reasons, setReasons] = useState({
    noTime: false,
    needExpertise: false,
    confidential: false,
    fullProcess: false,
    other: false,
  });

  const [errors, setErrors] = useState({});

  const [requestId, setRequestId] = useState(null);

  // null = show form | "pending" | "under_review" | "assigned"
  const [requestStatus, setRequestStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStatusChecked(false);
    setShowHistory(false);
    setForceForm(false);
    getHiringSupportStatus(dispatch)
      .catch(() => {})
      .finally(() => setStatusChecked(true));
    getHiringSupportHistory(dispatch).catch(() => {});
  }, [open, dispatch]);

  useEffect(() => {
    if (!reduxRequest) return;
    if (forceForm) return;
    const nextId = reduxRequest.id || reduxRequest.request_id;
    if (nextId) setRequestId(nextId);

    // Only set status if it's a recognised/active one
    if (reduxRequest.status && VALID_STATUSES.includes(reduxRequest.status)) {
      setRequestStatus(reduxRequest.status);
    } else {
      // Treat unknown or cancelled status as no active request — show form
      setRequestStatus(null);
    }
  }, [reduxRequest]);

  const reasonOptions = useMemo(() => [
    { key: "noTime", label: "No time to run the search" },
    { key: "needExpertise", label: "Need expertise identifying the right talent" },
    { key: "confidential", label: "Role is confidential" },
    { key: "fullProcess", label: "Prefer workbrook to manage the full process" },
    { key: "other", label: "Other" },
  ], []);

  const handleFormChange = ({ target: { name, value } }) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleReasonToggle = (key) =>
    setReasons((prev) => ({ ...prev, [key]: !prev[key] }));

  const validate = () => {
    const newErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!form.email.trim()) newErrors.email = "Email address is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = "Enter a valid email address";
    if (!form.roleTitle.trim()) newErrors.roleTitle = "Role title is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      cogoToast.error("Please fix the errors before submitting.", { position: "top-center", hideAfter: 3 });
      return;
    }

    try {
      const payload = {
        full_name: form.fullName,
        email: form.email,
        phone: form.phone,
        company_name: form.companyName,
        role_title: form.roleTitle,
        seniority: form.seniority,
        location: form.location,
        no_time: reasons.noTime,
        need_expertise: reasons.needExpertise,
        confidential: reasons.confidential,
        full_process: reasons.fullProcess,
        other: reasons.other,
        confidentiality: form.confidentiality,
        timeline: form.timeline,
        other_reason: form.otherReason,
      };

      const res = await submitHiringSupportRequest(dispatch, payload);
      if (res?.status) {
        cogoToast.success(res.message || "Request submitted! We'll reach out within 24 hours.", { position: "top-center" });
        setForceForm(false);
        setRequestStatus("pending");
      } else {
        throw new Error(res?.message || "Failed to submit request");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong. Please try again.";
      cogoToast.error(errorMessage, { position: "top-center", hideAfter: 4 });
    }
  };

  const handleCancelRequest = async () => {
    if (!requestId) {
      cogoToast.error("No request ID found.", { position: "top-center" });
      return;
    }

    try {
      const res = await cancelHiringSupportRequest(dispatch, requestId);
      if (res?.status) {
        cogoToast.success(res.message || "Your request has been cancelled.", { position: "top-center" });
        setRequestStatus(null);
        setRequestId(null);
      } else {
        throw new Error(res?.message || "Failed to cancel request");
      }
    } catch (error) {
      let errorMessage = error?.response?.data?.message || error?.message || "Failed to cancel request.";
      if (window.navigator.onLine && errorMessage === "Please check your internet connection") {
        errorMessage = "Unable to reach the server. Please try again.";
      }
      cogoToast.error(errorMessage, { position: "top-center", hideAfter: 4 });
    }
  };

  const handleRequestNew = () => {
    setForceForm(true);
    setRequestStatus(null);
    setShowHistory(false);
  };

  const handleViewHistory = () => {
    setShowHistory(true);
  };

  const handleBackFromHistory = () => {
    setShowHistory(false);
  };

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (open && !inline) {
      document.body.classList.add("rhs-pane-open");
    } else {
      document.body.classList.remove("rhs-pane-open");
    }

    return () => {
      document.body.classList.remove("rhs-pane-open");
    };
  }, [open, inline]);

  const isMobile = window.innerWidth <= 768;

  const renderContent = () => (
    <>
      <style>{`
        .ReactModal__Overlay.some-custom-overlay-class {
          z-index: 1200 !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          position: fixed !important;
        }

        .rhs-hiring-support-pane {
          z-index: 1201 !important;
        }

        .rhs-hiring-support-pane .slide-pane {
          z-index: 1201 !important;
          position: fixed !important;
          margin-top: 0 !important;
          height: 100vh !important;
          top: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
        }

        .rhs-hiring-support-pane .slide-pane__header {
          position: sticky !important;
          top: 0 !important;
          z-index: 100 !important;
          background: #fff !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .rhs-hiring-support-pane .slide-pane__content {
          padding: 0 !important;
          height: calc(100vh - 64px) !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          display: flex;
          flex-direction: column;
        }

        .rhs-sticky-footer {
          position: sticky;
          bottom: 0;
          z-index: 50;
        }

        body.rhs-pane-open {
          overflow: hidden !important;
        }

        body.rhs-pane-open #tawkchat-container,
        body.rhs-pane-open .tawk-min-container,
        body.rhs-pane-open .intercom-lightweight-app,
        body.rhs-pane-open #intercom-container,
        body.rhs-pane-open .crisp-client,
        body.rhs-pane-open #crisp-chatbox,
        body.rhs-pane-open .chat-widget,
        body.rhs-pane-open .chatbot,
        body.rhs-pane-open [data-testid="launcher"],
        body.rhs-pane-open [data-chat-widget],
        body.rhs-pane-open iframe[src*="tawk"],
        body.rhs-pane-open iframe[src*="crisp"],
        body.rhs-pane-open iframe[src*="intercom"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }

        @media (max-width: 768px) {
          .rhs-hiring-support-pane .slide-pane {
            height: 100vh !important;
            top: 0 !important;
          }

          .rhs-hiring-support-pane .slide-pane__header {
            position: sticky !important;
            top: 0 !important;
            z-index: 100 !important;
          }

          .rhs-hiring-support-pane .slide-pane__content {
            height: calc(100vh - 56px) !important;
          }

          .rhs-sticky-footer {
            position: sticky !important;
            bottom: 0 !important;
            z-index: 50 !important;
          }
        }
      `}</style>
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

        {/* ── INITIAL STATUS CHECK ── */}
        {!statusChecked ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
            <div style={{ fontSize: "13px", color: "#6b7280" }}>Checking your request status…</div>
          </div>
        ) : showHistory ? (
          /* ── HISTORY VIEW ── */
          <div style={{ flex: 1, overflowY: "auto" }}>
            <HistoryView
              history={history}
              loading={loadingHistory}
              onBack={handleBackFromHistory}
              onClose={handleClose}
            />
          </div>
        ) : requestStatus ? (
          /* ── STATUS VIEW ── */
          <div style={{ flex: 1, overflowY: "auto" }}>
            <StatusView
              status={requestStatus}
              onCancel={handleCancelRequest}
              onClose={handleClose}
              onRequestNew={handleRequestNew}
              onViewHistory={handleViewHistory}
              cancelling={cancelling}
            />
          </div>
        ) : (
          /* ── FORM VIEW ── */
          <>
            {!inline && (
              <div style={{ textAlign: "center", marginBottom: "20px", marginTop: "20px" }}>
                <button
                  type="button"
                  onClick={handleViewHistory}
                  style={{
                    ...btnBase,
                    background: "#3A6ED4",
                    border: "1.5px solid #d1d5db",
                    color: "#fff",
                    fontSize: "12px",
                  }}
                >
                  View Request History
                </button>
              </div>
            )}
            <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "16px" : "20px 24px 0" }}>
              <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "20px", marginTop: 0 }}>
                Share a few details and a workbrook advisor will reach out within 24 hours.
              </p>

              <form id="rhs-form" onSubmit={handleSubmit}>

                {/* ── Row 1 ── */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: isMobile ? "0" : "0 32px",
                }}>
                  {/* Contact */}
                  <div>
                    <div style={sectionHead}>Contact</div>
                    <Field label="Full Name" required error={errors.fullName}>
                      <TextInput name="fullName" value={form.fullName} onChange={handleFormChange} required hasError={!!errors.fullName} />
                    </Field>
                    <Field label="Email Address" required error={errors.email}>
                      <TextInput name="email" type="email" value={form.email} onChange={handleFormChange} required hasError={!!errors.email} />
                    </Field>
                    <Field label="Phone Number (optional)">
                      <TextInput name="phone" value={form.phone} onChange={handleFormChange} />
                    </Field>
                    <Field label="Company Name (optional)">
                      <TextInput name="companyName" value={form.companyName} onChange={handleFormChange} />
                    </Field>
                  </div>

                  {/* Role Overview */}
                  <div>
                    <div style={sectionHead}>Role Overview</div>
                    <Field label="Role Title" required error={errors.roleTitle}>
                      <TextInput name="roleTitle" value={form.roleTitle} onChange={handleFormChange} required hasError={!!errors.roleTitle} />
                    </Field>
                    <Field label="Seniority Level" required error={errors.seniority}>
                      <SelectInput name="seniority" value={form.seniority} onChange={handleFormChange} required hasError={!!errors.seniority}>
                        <option value="">Select level...</option>
                        <option value="Entry Level">Entry Level</option>
                        <option value="Mid Level">Mid Level</option>
                        <option value="Senior Level">Senior Level</option>
                        <option value="Manager/Director">Manager/Director</option>
                        <option value="Executive/C-Suite">Executive / C-Suite</option>
                      </SelectInput>
                    </Field>
                    <Field label="Location / Work Type" required error={errors.location}>
                      <TextInput name="location" value={form.location} onChange={handleFormChange} placeholder="e.g. Remote, New York, etc." required hasError={!!errors.location} />
                    </Field>
                  </div>
                </div>

                <hr style={{ margin: "16px 0", border: 0, borderTop: "1px solid #e5e7eb" }} />

                {/* ── Row 2 ── */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: isMobile ? "0" : "0 32px",
                }}>
                  {/* Details */}
                  <div>
                    <div style={sectionHead}>Details</div>
                    <Field label="Is this a confidential search?">
                      <SelectInput name="confidentiality" value={form.confidentiality} onChange={handleFormChange}>
                        <option value="">Select...</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </SelectInput>
                    </Field>
                    <Field label="Hiring Timeline">
                      <SelectInput name="timeline" value={form.timeline} onChange={handleFormChange}>
                        <option value="">Select timeline...</option>
                        <option value="ASAP">ASAP (Urgent)</option>
                        <option value="1-3 Months">1 - 3 Months</option>
                        <option value="3+ Months">3+ Months / Exploring</option>
                      </SelectInput>
                    </Field>
                  </div>

                  {/* Why do you need help? */}
                  <div>
                    <div style={sectionHead}>Why do you need help? (optional)</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "4px" }}>
                      {reasonOptions.map((opt) => (
                        <CheckItem
                          key={opt.key}
                          label={opt.label}
                          checked={reasons[opt.key]}
                          onChange={() => handleReasonToggle(opt.key)}
                        />
                      ))}
                      
                      {reasons.other && (
                        <div style={{ marginTop: "4px" }}>
                          <TextInput 
                            name="otherReason" 
                            value={form.otherReason} 
                            onChange={handleFormChange} 
                            placeholder="Please specify..." 
                            style={{ 
                              background: "#f9fafb", 
                              border: "1px dashed #d1d5db"
                            }} 
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* extra space so the form isn't hidden behind the sticky footer */}
                <div style={{ height: "40px" }} />
              </form>
            </div>

            {/* ── Sticky Footer ── */}
            <div className="rhs-sticky-footer" style={{
              borderTop: "1px solid #e5e7eb",
              padding: isMobile ? "12px 16px" : "14px 24px",
              paddingBottom: isMobile ? "calc(12px + env(safe-area-inset-bottom, 0px))" : "34px",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
              flexWrap: isMobile ? "wrap" : "nowrap",
              gap: isMobile ? "10px" : 0,
            }}>
              {/* Confidentiality note */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C8.13 2 5 5.13 5 9v2H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2v-9a2 2 0 00-2-2h-1V9c0-3.87-3.13-7-7-7zm0 2c2.76 0 5 2.24 5 5v2H7V9c0-2.76 2.24-5 5-5zm0 10a2 2 0 110 4 2 2 0 010-4z" fill="#9ca3af"/>
                </svg>
                <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                  Confidential · Response within 24 hours
                </span>
              </div>

              {/* Buttons */}
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                {!inline && (
                  <button
                    type="button"
                    onClick={handleClose}
                    style={{ ...btnBase, background: "#fff", border: "1.5px solid #d1d5db", color: "#374151" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#6b7280"; e.currentTarget.style.color = "#111827"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#374151"; }}
                  >
                    Cancel
                  </button>
                )}

                <button
                  type="submit"
                  form="rhs-form"
                  disabled={submitting}
                  style={{
                    ...btnBase,
                    background: submitting ? "#93afd8" : "#3A6ED4",
                    border: "none",
                    color: "#fff",
                    padding: "0 24px",
                    cursor: (submitting || loadingStatus) ? "not-allowed" : "pointer",
                  }}
                  onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = "#2f5db8"; }}
                  onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.background = "#3A6ED4"; }}
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );

  return inline ? (
    <div className="inline-form-container">
      {renderContent()}
    </div>
  ) : (
    <SlidingPane
      className="rhs-hiring-support-pane"
      overlayClassName="some-custom-overlay-class"
      isOpen={open}
      title="Request Hiring Support"
      subtitle="Confidential. Fast. Tailored to your needs."
      width={window.innerWidth <= 768 ? "100%" : window.innerWidth <= 1024 ? "100%" : "90%"}
      from="right"
      onRequestClose={handleClose}
    >
      {renderContent()}
    </SlidingPane>
  );
};

RequestHiringSupport.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
};

export default RequestHiringSupport;